<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="900"
    content-class="twilio-auth-dialog"
    scrollable
    :retain-focus="false"
  >
    <v-stepper v-model="step" class="modern-stepper" elevation="0">
      <!-- Stepper Header -->
      <v-stepper-header class="modern-stepper-header">
        <v-stepper-step :complete="step > 1" step="1" color="primary">
          <span class="step-label">Connect Twilio</span>
        </v-stepper-step>
        <v-divider></v-divider>

        <v-stepper-step :complete="step > 2" step="2" color="primary">
          <span class="step-label">SIP Trunk</span>
        </v-stepper-step>
        <v-divider></v-divider>

        <v-stepper-step :complete="step > 3" step="3" color="primary">
          <span class="step-label">Phone Numbers</span>
        </v-stepper-step>
        <v-divider></v-divider>

        <v-stepper-step step="4" color="primary">
          <span class="step-label">Complete</span>
        </v-stepper-step>
      </v-stepper-header>

      <v-stepper-items>
        <!-- Step 1: Connect Twilio Account -->
        <v-stepper-content step="1">
          <v-card flat class="setup-card">
            <v-card-text class="pa-6">
              <div class="text-center mb-6">
                <div class="step-badge mb-4">
                  <v-chip
                    color="primary"
                    small
                    outlined
                    class="font-weight-bold"
                  >
                    <v-icon x-small left>mdi-link-variant</v-icon>
                    STEP 1
                  </v-chip>
                </div>
                <h1 class="modern-h1 mb-3">Connect Your Twilio Account</h1>
                <p class="modern-p">
                  Authenticate with Twilio to automatically configure your cloud
                  calling
                </p>
              </div>

              <v-row justify="center">
                <v-col cols="12" md="8">
                  <!-- Connected State - Matching welcomePopup exactly -->
                  <v-card
                    v-if="twilioConnected"
                    elevation="3"
                    class="text-center pa-6 twilio-form-card"
                  >
                    <v-avatar color="success" size="80" class="mb-4">
                      <v-icon size="40" color="white">mdi-check-circle</v-icon>
                    </v-avatar>
                    <h3 class="modern-h3 mb-2">Twilio Connected!</h3>
                    <p class="modern-p mb-4">
                      Your Twilio account has been successfully linked.
                    </p>
                    <v-chip color="success" text-color="white" class="mb-4">
                      <v-icon small left>mdi-account-check</v-icon>
                      {{
                        twilioAccountInfo
                          ? twilioAccountInfo.friendlyName
                          : "Connected"
                      }}
                    </v-chip>
                    <p class="caption grey--text">
                      Account SID:
                      {{
                        twilioAccountInfo
                          ? twilioAccountInfo.accountSid.substring(0, 10) +
                            "..."
                          : ""
                      }}
                    </p>

                    <!-- Show connected phone numbers -->
                    <div
                      v-if="twilioPhoneNumbers && twilioPhoneNumbers.length > 0"
                      class="mt-4 text-center"
                    >
                      <v-divider class="mb-3"></v-divider>
                      <p class="subtitle-2 font-weight-bold mb-2">
                        <v-icon small color="success" class="mr-1"
                          >mdi-phone-check</v-icon
                        >
                        Your Phone Numbers ({{ twilioPhoneNumbers.length }})
                      </p>
                      <div class="d-flex justify-center flex-wrap">
                        <v-chip
                          v-for="num in twilioPhoneNumbers"
                          :key="num.sid || num.phoneNumber"
                          small
                          color="success"
                          text-color="white"
                          class="ma-1"
                        >
                          <v-icon small left>mdi-phone</v-icon>
                          {{ num.phoneNumber }}
                        </v-chip>
                      </div>
                    </div>

                    <!-- Show SIP Domains if available -->
                    <div
                      v-if="twilioSipDomains && twilioSipDomains.length > 0"
                      class="mt-3 text-center"
                    >
                      <p class="subtitle-2 font-weight-bold mb-2">
                        <v-icon small color="primary" class="mr-1"
                          >mdi-server</v-icon
                        >
                        SIP Domains ({{ twilioSipDomains.length }})
                      </p>
                      <div class="d-flex justify-center flex-wrap">
                        <v-chip
                          v-for="domain in twilioSipDomains"
                          :key="domain.sid"
                          small
                          outlined
                          color="primary"
                          class="ma-1"
                        >
                          {{ domain.domainName }}
                        </v-chip>
                      </div>
                    </div>

                    <!-- Show Elastic SIP Trunks if available -->
                    <div
                      v-if="
                        twilioElasticTrunks && twilioElasticTrunks.length > 0
                      "
                      class="mt-3"
                    >
                      <p class="subtitle-2 font-weight-bold mb-2">
                        <v-icon small color="primary" class="mr-1"
                          >mdi-transit-connection-variant</v-icon
                        >
                        Elastic SIP Trunks ({{ twilioElasticTrunks.length }})
                      </p>
                      <v-list dense class="text-left">
                        <v-list-item
                          v-for="trunk in twilioElasticTrunks"
                          :key="trunk.sid"
                          class="px-2"
                        >
                          <v-list-item-icon class="mr-2">
                            <v-icon color="success" small
                              >mdi-check-circle</v-icon
                            >
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="subtitle-2">{{
                              trunk.friendlyName
                            }}</v-list-item-title>
                            <v-list-item-subtitle class="caption">
                              Domain: {{ trunk.domainName }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-action>
                            <v-btn
                              x-small
                              outlined
                              color="primary"
                              :href="
                                'https://console.twilio.com/us1/develop/sip-trunking/trunks/' +
                                  trunk.sid
                              "
                              target="_blank"
                            >
                              <v-icon x-small>mdi-open-in-new</v-icon>
                            </v-btn>
                          </v-list-item-action>
                        </v-list-item>
                      </v-list>
                    </div>

                    <!-- Show carrier config status -->
                    <div
                      v-if="
                        twilioCarrier &&
                          twilioCarrier.type === 'twilio-elastic-trunk'
                      "
                      class="mt-3"
                    >
                      <v-divider class="mb-3"></v-divider>
                      <v-alert type="success" dense text>
                        <strong>Ready for SIP Calling!</strong>
                        <p class="mb-0 caption">
                          Your Elastic SIP Trunk is configured for GoDial.
                        </p>
                      </v-alert>
                    </div>
                  </v-card>

                  <!-- Not Connected State - Twilio Connect OAuth -->
                  <v-card
                    v-else-if="!twilioOAuthAccountSid"
                    elevation="3"
                    class="pa-6 twilio-form-card"
                  >
                    <div class="text-center">
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/7/7e/Twilio-logo-red.svg"
                        height="50"
                        alt="Twilio"
                        class="mb-4"
                      />
                      <h3 class="modern-h3 mb-2">
                        Connect Your Twilio Account
                      </h3>
                      <p class="modern-p mb-5">
                        Click the button below to securely connect your Twilio
                        account
                      </p>

                      <!-- Main One-Click Connect Button -->
                      <v-btn
                        color="#F22F46"
                        x-large
                        depressed
                        dark
                        class="mb-4 text-capitalize twilio-connect-btn"
                        :loading="twilioConnecting"
                        @click="connectWithTwilioOAuth"
                      >
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/7/7e/Twilio-logo-red.svg"
                          height="24"
                          alt="Twilio"
                          class="mr-3"
                          style="filter: brightness(0) invert(1)"
                        />
                        Connect with Twilio
                      </v-btn>

                      <p class="caption grey--text mb-4">
                        You'll be redirected to Twilio to authorize GoDial
                      </p>

                      <v-alert
                        v-if="twilioError"
                        type="error"
                        dense
                        text
                        class="mt-3 text-left"
                      >
                        {{ twilioError }}
                      </v-alert>
                    </div>
                  </v-card>

                  <!-- OAuth In Progress State -->
                  <v-card v-else elevation="3" class="pa-6 twilio-form-card">
                    <div class="text-center">
                      <v-avatar color="primary" size="80" class="mb-4">
                        <v-icon v-if="!twilioConnecting" size="40" color="white"
                          >mdi-account-check</v-icon
                        >
                        <v-progress-circular
                          v-else
                          indeterminate
                          color="white"
                          size="40"
                        ></v-progress-circular>
                      </v-avatar>
                      <h3 class="modern-h3 mb-2">
                        {{
                          twilioConnecting
                            ? "Connecting..."
                            : "Account Verified!"
                        }}
                      </h3>
                      <p class="modern-p mb-2">
                        {{
                          twilioConnecting
                            ? "Setting up your Twilio integration..."
                            : "Your Twilio account has been authorized."
                        }}
                      </p>
                      <v-chip color="primary" outlined class="mb-4">
                        <v-icon small left>mdi-identifier</v-icon>
                        {{
                          twilioOAuthAccountSid
                            ? twilioOAuthAccountSid.substring(0, 10) + "..."
                            : ""
                        }}
                      </v-chip>

                      <v-progress-linear
                        v-if="twilioConnecting"
                        indeterminate
                        color="primary"
                        class="mb-4"
                      ></v-progress-linear>

                      <v-alert
                        v-if="twilioError"
                        type="error"
                        dense
                        text
                        class="mt-3 text-left"
                      >
                        {{ twilioError }}
                        <div class="mt-2">
                          <v-btn
                            color="error"
                            small
                            depressed
                            @click="completeTwilioOAuthConnection"
                          >
                            <v-icon small left>mdi-refresh</v-icon>
                            Try Again
                          </v-btn>
                        </div>
                      </v-alert>

                      <v-btn
                        v-if="!twilioConnecting"
                        text
                        small
                        color="grey"
                        class="text-capitalize mt-2"
                        block
                        @click="twilioOAuthAccountSid = null"
                      >
                        <v-icon small left>mdi-arrow-left</v-icon>
                        Start Over
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions class="dialog-footer pa-4">
              <v-btn
                text
                class="text-capitalize cancel-btn"
                @click="closeDialog"
              >
                <v-icon left small>mdi-close</v-icon>
                Cancel
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                large
                depressed
                class="continue-btn text-capitalize"
                :disabled="!twilioConnected"
                @click="goToStep2"
              >
                Continue
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content>

        <!-- Step 2: SIP Trunk Setup -->
        <v-stepper-content step="2">
          <v-card flat class="setup-card">
            <v-card-text class="pa-6">
              <div class="text-center mb-6">
                <div class="step-badge mb-4">
                  <v-chip
                    color="primary"
                    small
                    outlined
                    class="font-weight-bold"
                  >
                    <v-icon x-small left>mdi-transit-connection-variant</v-icon>
                    STEP 2
                  </v-chip>
                </div>
                <h1 class="modern-h1 mb-3">Elastic SIP Trunk</h1>
                <p class="modern-p">
                  Configure your SIP trunk for making and receiving calls
                </p>
              </div>

              <v-row justify="center">
                <v-col cols="12" md="10">
                  <!-- Loading State -->
                  <v-card
                    v-if="trunkLoading"
                    elevation="3"
                    class="pa-6 text-center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="50"
                      class="mb-4"
                    ></v-progress-circular>
                    <p class="modern-p mb-0">Loading SIP Trunks...</p>
                  </v-card>

                  <!-- Main Content -->
                  <v-card v-else elevation="3" class="pa-0 overflow-hidden">
                    <!-- Two Option Tabs -->
                    <v-tabs
                      v-model="trunkMode"
                      centered
                      grow
                      background-color="grey lighten-4"
                      slider-color="primary"
                    >
                      <v-tab
                        href="#existing"
                        :disabled="
                          !twilioElasticTrunks ||
                            twilioElasticTrunks.length === 0
                        "
                      >
                        <v-icon small left>mdi-format-list-bulleted</v-icon>
                        Use Existing Trunk
                        <v-chip
                          v-if="
                            twilioElasticTrunks &&
                              twilioElasticTrunks.length > 0
                          "
                          x-small
                          class="ml-2"
                          color="primary"
                          outlined
                        >
                          {{ twilioElasticTrunks.length }}
                        </v-chip>
                      </v-tab>
                      <v-tab href="#create">
                        <v-icon small left>mdi-plus-circle</v-icon>
                        Create New Trunk
                      </v-tab>
                    </v-tabs>

                    <v-tabs-items v-model="trunkMode">
                      <!-- Existing Trunks Tab -->
                      <v-tab-item value="existing">
                        <div class="pa-5">
                          <!-- No trunks message -->
                          <div
                            v-if="
                              !twilioElasticTrunks ||
                                twilioElasticTrunks.length === 0
                            "
                            class="text-center py-6"
                          >
                            <v-avatar
                              color="grey lighten-2"
                              size="60"
                              class="mb-3"
                            >
                              <v-icon size="30" color="grey"
                                >mdi-transit-connection-variant</v-icon
                              >
                            </v-avatar>
                            <h3 class="modern-h3 mb-2 grey--text">
                              No Existing Trunks
                            </h3>
                            <p class="modern-p grey--text mb-4">
                              You don't have any SIP trunks yet. Create a new
                              one to get started.
                            </p>
                            <v-btn
                              color="primary"
                              outlined
                              @click="trunkMode = 'create'"
                            >
                              <v-icon left>mdi-plus</v-icon>
                              Create New Trunk
                            </v-btn>
                          </div>

                          <!-- Trunk list -->
                          <div v-else>
                            <div class="d-flex align-center mb-4">
                              <p class="modern-p mb-0 grey--text">
                                Select a trunk to use with GoDial
                              </p>
                              <v-spacer></v-spacer>
                              <v-btn
                                text
                                x-small
                                color="primary"
                                :loading="trunkRefreshing"
                                @click="refreshElasticTrunks"
                              >
                                <v-icon x-small left>mdi-refresh</v-icon>
                                Refresh
                              </v-btn>
                            </div>

                            <v-radio-group
                              v-model="selectedTrunkSid"
                              class="mt-0"
                              hide-details
                            >
                              <v-card
                                v-for="trunk in twilioElasticTrunks"
                                :key="trunk.sid"
                                :outlined="selectedTrunkSid !== trunk.sid"
                                :color="
                                  selectedTrunkSid === trunk.sid
                                    ? 'primary lighten-5'
                                    : ''
                                "
                                :class="[
                                  'mb-3 trunk-selection-card',
                                  {
                                    'selected-trunk-card':
                                      selectedTrunkSid === trunk.sid,
                                  },
                                ]"
                                style="cursor: pointer"
                                @click="selectedTrunkSid = trunk.sid"
                              >
                                <v-card-text class="pa-4">
                                  <div class="d-flex align-center">
                                    <v-radio
                                      :value="trunk.sid"
                                      class="mr-3 mt-0"
                                      @click.stop
                                    ></v-radio>
                                    <div class="flex-grow-1">
                                      <div class="d-flex align-center mb-1">
                                        <v-icon
                                          color="primary"
                                          small
                                          class="mr-2"
                                          >mdi-transit-connection-variant</v-icon
                                        >
                                        <span
                                          class="subtitle-1 font-weight-bold"
                                          >{{ trunk.friendlyName }}</span
                                        >
                                      </div>
                                      <p class="caption grey--text mb-0">
                                        <v-icon x-small class="mr-1"
                                          >mdi-identifier</v-icon
                                        >
                                        {{ trunk.sid }}
                                      </p>
                                    </div>
                                    <v-btn
                                      icon
                                      x-small
                                      color="primary"
                                      :href="
                                        'https://www.twilio.com/console/sip-trunking/trunks/' +
                                          trunk.sid
                                      "
                                      target="_blank"
                                      @click.stop
                                    >
                                      <v-icon small>mdi-open-in-new</v-icon>
                                    </v-btn>
                                  </div>
                                </v-card-text>
                              </v-card>
                            </v-radio-group>
                          </div>
                        </div>
                      </v-tab-item>

                      <!-- Create New Trunk Tab -->
                      <v-tab-item value="create">
                        <div class="pa-5">
                          <div class="text-center mb-5">
                            <v-avatar
                              color="primary lighten-4"
                              size="60"
                              class="mb-3"
                            >
                              <v-icon size="30" color="primary"
                                >mdi-plus-circle</v-icon
                              >
                            </v-avatar>
                            <h3 class="modern-h3 mb-2">
                              Create New SIP Trunk
                            </h3>
                            <p class="modern-p grey--text">
                              Set up a new Elastic SIP Trunk on your Twilio
                              account
                            </p>
                          </div>

                          <v-alert type="info" dense text class="mb-4">
                            <strong>What is an Elastic SIP Trunk?</strong>
                            <p class="mb-0 caption">
                              Elastic SIP Trunks allow you to make and receive
                              calls through Twilio's global network using the
                              SIP protocol.
                            </p>
                          </v-alert>

                          <v-form ref="createTrunkForm" class="text-left">
                            <v-text-field
                              v-model="newTrunkName"
                              label="Trunk Name *"
                              placeholder="e.g., GoDial Business Trunk"
                              outlined
                              dense
                              prepend-inner-icon="mdi-label"
                              class="mb-3"
                              :rules="[(v) => !!v || 'Trunk name is required']"
                            ></v-text-field>

                            <v-btn
                              color="primary"
                              block
                              large
                              depressed
                              :loading="trunkCreating"
                              :disabled="!newTrunkName"
                              @click="createNewElasticTrunk"
                            >
                              <v-icon left>mdi-plus</v-icon>
                              Create Elastic SIP Trunk
                            </v-btn>
                          </v-form>

                          <div
                            v-if="
                              twilioElasticTrunks &&
                                twilioElasticTrunks.length > 0
                            "
                            class="text-center mt-4 pt-4"
                            style="border-top: 1px dashed #e0e0e0"
                          >
                            <p class="caption grey--text mb-2">
                              <v-icon x-small class="mr-1"
                                >mdi-information-outline</v-icon
                              >
                              You have
                              {{ twilioElasticTrunks.length }} existing trunk(s)
                            </p>
                            <v-btn
                              text
                              small
                              color="primary"
                              @click="trunkMode = 'existing'"
                            >
                              Use Existing Trunk Instead
                            </v-btn>
                          </div>
                        </div>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>

                  <!-- Error Alert -->
                  <v-alert
                    v-if="trunkError"
                    type="error"
                    dense
                    text
                    class="mt-4"
                    dismissible
                    @input="trunkError = null"
                  >
                    {{ trunkError }}
                  </v-alert>

                  <!-- Success Alert -->
                  <v-alert
                    v-if="trunkSuccess"
                    type="success"
                    dense
                    text
                    class="mt-4"
                    dismissible
                    @input="trunkSuccess = null"
                  >
                    {{ trunkSuccess }}
                  </v-alert>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions class="dialog-footer pa-4">
              <v-btn
                text
                class="text-capitalize cancel-btn"
                :disabled="trunkConfiguring"
                @click="step = 1"
              >
                <v-icon left small>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                large
                depressed
                class="continue-btn text-capitalize"
                :disabled="!selectedTrunkSid && trunkMode === 'existing'"
                :loading="trunkConfiguring"
                @click="useSelectedTrunk"
              >
                Continue
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content>

        <!-- Step 3: Phone Numbers & DID Configuration -->
        <v-stepper-content step="3">
          <v-card flat class="setup-card">
            <v-card-text class="pa-6">
              <div class="text-center mb-4">
                <div class="step-badge mb-4">
                  <v-chip
                    color="primary"
                    small
                    outlined
                    class="font-weight-bold"
                  >
                    <v-icon x-small left>mdi-phone-settings</v-icon>
                    STEP 3
                  </v-chip>
                </div>
                <h1 class="modern-h1 mb-2">Phone Numbers & Routing</h1>
                <p class="modern-p grey--text">
                  Configure your phone numbers and inbound call routing
                </p>
              </div>

              <v-row justify="center">
                <v-col cols="12" md="10">
                  <!-- Loading State -->
                  <v-card
                    v-if="phoneNumbersLoading"
                    elevation="3"
                    class="pa-6 text-center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="50"
                      class="mb-4"
                    ></v-progress-circular>
                    <h3 class="modern-h3">Loading Phone Numbers...</h3>
                    <p class="modern-p grey--text">
                      Fetching your Twilio phone numbers
                    </p>
                  </v-card>

                  <!-- Phone Numbers Configuration -->
                  <v-card v-else elevation="3" class="pa-6">
                    <div class="d-flex align-center justify-space-between mb-4">
                      <div>
                        <h3 class="modern-h3 mb-1">Phone Numbers</h3>
                        <p class="caption grey--text mb-0">
                          Select numbers to use with GoDial PBX
                        </p>
                      </div>
                      <div class="d-flex align-center">
                        <v-btn
                          small
                          outlined
                          color="primary"
                          class="mr-2"
                          :loading="phoneNumbersRefreshing"
                          @click="refreshPhoneNumbers"
                        >
                          <v-icon small left>mdi-refresh</v-icon>
                          Refresh
                        </v-btn>
                        <v-btn
                          small
                          color="primary"
                          href="https://console.twilio.com/us1/develop/phone-numbers/manage/search"
                          target="_blank"
                        >
                          <v-icon small left>mdi-plus</v-icon>
                          Buy Number
                        </v-btn>
                      </div>
                    </div>

                    <v-divider class="mb-4"></v-divider>

                    <!-- Phone Numbers List -->
                    <div
                      v-if="twilioPhoneNumbers && twilioPhoneNumbers.length > 0"
                    >
                      <!-- Show phone numbers based on trunk status -->
                      <div v-if="availablePhoneNumbers.length > 0">
                        <!-- Info about selection mode -->
                        <v-alert
                          v-if="!trunkHasConnectedNumbers"
                          type="info"
                          dense
                          text
                          class="mb-3"
                        >
                          <strong>Select One Number</strong>
                          <p class="mb-0 caption mt-1">
                            Choose a phone number to connect to this trunk. The number will be automatically assigned when you continue.
                          </p>
                        </v-alert>

                        <!-- Radio group for single selection when trunk has no numbers -->
                        <v-radio-group
                          v-if="!trunkHasConnectedNumbers"
                          v-model="selectedPhoneNumbers[0]"
                          hide-details
                          class="mt-0"
                          @change="(val) => selectedPhoneNumbers = val ? [val] : []"
                        >
                          <v-card
                            v-for="number in availablePhoneNumbers"
                            :key="number.sid"
                            outlined
                            class="mb-3 phone-number-card"
                            :class="{
                              'selected-number': selectedPhoneNumbers.includes(
                                number.phoneNumber
                              ),
                            }"
                          >
                            <v-card-text class="pa-4">
                              <div class="d-flex align-start">
                                <v-radio
                                  :value="number.phoneNumber"
                                  hide-details
                                  class="mr-3 mt-0 pt-0 flex-shrink-0"
                                >
                                </v-radio>
                                <div class="flex-grow-1 min-width-0">
                                  <div class="d-flex align-center flex-wrap">
                                    <v-icon color="primary" small class="mr-2"
                                      >mdi-phone</v-icon
                                    >
                                    <span
                                      class="subtitle-1 font-weight-bold phone-number-text"
                                      >{{ number.phoneNumber }}</span
                                    >
                                  </div>
                                  <div class="mt-1">
                                    <v-chip
                                      x-small
                                      color="info"
                                      outlined
                                      class="phone-status-chip"
                                    >
                                      <v-icon x-small left>mdi-link-off</v-icon>
                                      Available
                                    </v-chip>
                                  </div>
                                  <p class="caption grey--text mb-0 mt-1">
                                    {{
                                      number.friendlyName || number.phoneNumber
                                    }}
                                  </p>
                                </div>
                              </div>
                            </v-card-text>
                          </v-card>
                        </v-radio-group>

                        <!-- Show connected numbers when trunk already has numbers -->
                        <div v-else>
                          <v-card
                            v-for="number in availablePhoneNumbers"
                            :key="number.sid"
                            outlined
                            class="mb-3 phone-number-card selected-number"
                          >
                            <v-card-text class="pa-4">
                              <div class="d-flex align-start">
                                <v-checkbox
                                  v-model="selectedPhoneNumbers"
                                  :value="number.phoneNumber"
                                  disabled
                                  hide-details
                                  class="mr-3 mt-0 pt-0 flex-shrink-0"
                                >
                                </v-checkbox>
                                <div class="flex-grow-1 min-width-0">
                                  <div class="d-flex align-center flex-wrap">
                                    <v-icon color="primary" small class="mr-2"
                                      >mdi-phone</v-icon
                                    >
                                    <span
                                      class="subtitle-1 font-weight-bold phone-number-text"
                                      >{{ number.phoneNumber }}</span
                                    >
                                  </div>
                                  <div class="mt-1">
                                    <v-chip
                                      x-small
                                      color="success"
                                      class="phone-status-chip"
                                    >
                                      <v-icon x-small left>mdi-check</v-icon>
                                      Connected
                                    </v-chip>
                                  </div>
                                  <p class="caption grey--text mb-0 mt-1">
                                    {{
                                      number.friendlyName || number.phoneNumber
                                    }}
                                  </p>
                                </div>
                              </div>
                            </v-card-text>
                          </v-card>
                        </div>
                      </div>

                      <!-- Message when no valid numbers available -->
                      <v-alert v-else type="warning" dense text class="mb-3">
                        <strong>No Available Numbers</strong>
                        <p class="mb-0 caption">
                          All your phone numbers are connected to other trunks.
                          You need to either disconnect them or purchase a new
                          number to proceed.
                        </p>
                      </v-alert>
                    </div>

                    <v-alert v-else type="warning" dense text>
                      <div class="d-flex align-center justify-space-between">
                        <div>
                          <strong
                            >No phone numbers found in your Twilio
                            account.</strong
                          >
                          <p class="mb-0 caption mt-1">
                            Please purchase a phone number to continue with
                            cloud calling setup.
                          </p>
                        </div>
                        <v-btn
                          small
                          color="primary"
                          href="https://console.twilio.com/us1/develop/phone-numbers/manage/search"
                          target="_blank"
                          class="ml-3"
                        >
                          <v-icon small left>mdi-cart-plus</v-icon>
                          Buy Number
                        </v-btn>
                      </div>
                    </v-alert>

                    <v-divider class="my-4"></v-divider>

                    <!-- DID Routing Configuration -->
                    <div class="mb-4">
                      <h3 class="modern-h3 mb-2">
                        <v-icon color="primary" class="mr-2"
                          >mdi-call-split</v-icon
                        >
                        Inbound Call Routing
                      </h3>
                      <p class="caption grey--text mb-3">
                        Configure how incoming calls are routed to agents
                      </p>

                      <v-select
                        v-model="defaultRoutingType"
                        :items="routingTypes"
                        item-text="text"
                        item-value="value"
                        label="Default Routing Strategy"
                        outlined
                        dense
                        prepend-inner-icon="mdi-route"
                        class="mb-3"
                      >
                        <template v-slot:item="{ item }">
                          <v-icon class="mr-3">{{ item.icon }}</v-icon>
                          <div>
                            <div class="subtitle-2">{{ item.text }}</div>
                            <div class="caption grey--text">
                              {{ item.description }}
                            </div>
                          </div>
                        </template>
                      </v-select>

                      <v-alert
                        v-if="defaultRoutingType === 'agent'"
                        type="info"
                        dense
                        text
                        class="mb-3"
                      >
                        <strong>Single Agent:</strong> Calls will be routed to a
                        specific agent. You can configure this per-number later.
                      </v-alert>
                      <v-alert
                        v-else-if="defaultRoutingType === 'random-agent'"
                        type="info"
                        dense
                        text
                        class="mb-3"
                      >
                        <strong>Random Agent:</strong> Calls will be randomly
                        distributed among available agents.
                      </v-alert>
                    </div>

                    <!-- Error Alert -->
                    <v-alert
                      v-if="phoneNumbersError"
                      type="error"
                      dense
                      text
                      class="mt-4"
                    >
                      {{ phoneNumbersError }}
                    </v-alert>

                    <!-- Success Alert -->
                    <v-alert
                      v-if="phoneNumbersSuccess"
                      type="success"
                      dense
                      text
                      class="mt-4"
                    >
                      {{ phoneNumbersSuccess }}
                    </v-alert>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions class="dialog-footer pa-4">
              <v-btn
                text
                class="text-capitalize cancel-btn"
                :disabled="phoneNumbersSaving"
                @click="step = 2"
              >
                <v-icon left small>mdi-arrow-left</v-icon>
                Back
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                large
                depressed
                class="continue-btn text-capitalize"
                :disabled="
                  availablePhoneNumbers.length === 0 ||
                    selectedPhoneNumbers.length === 0
                "
                :loading="phoneNumbersSaving"
                @click="savePhoneNumbersConfig"
              >
                Continue
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-stepper-content>

        <!-- Step 4: Complete -->
        <v-stepper-content step="4">
          <v-card flat class="setup-card">
            <v-card-text class="pa-6">
              <div class="text-center mb-5">
                <v-avatar color="success" size="100" class="mb-4">
                  <v-icon size="50" color="white"
                    >mdi-check-circle-outline</v-icon
                  >
                </v-avatar>
                <h1 class="modern-h1 mb-3">🎉 All Set!</h1>
                <p class="modern-p">
                  Your Twilio account has been successfully configured with
                  GoDial
                </p>
              </div>

              <v-row justify="center">
                <v-col cols="12" md="8">
                  <v-card elevation="3" class="pa-5">
                    <h3 class="modern-h3 mb-4 text-center">
                      <v-icon color="primary" class="mr-2"
                        >mdi-format-list-checks</v-icon
                      >
                      What's Configured
                    </h3>

                    <v-list dense class="transparent">
                      <v-list-item>
                        <v-list-item-icon class="mr-3">
                          <v-icon color="success">mdi-check-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">
                            Twilio Account Connected
                          </v-list-item-title>
                          <v-list-item-subtitle class="caption">
                            {{ twilioAccountInfo.accountSid }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-if="selectedTrunkSid">
                        <v-list-item-icon class="mr-3">
                          <v-icon color="success">mdi-check-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">
                            SIP Trunk Configured
                          </v-list-item-title>
                          <v-list-item-subtitle class="caption">
                            {{ getSelectedTrunkName() }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>

                      <v-list-item v-if="selectedPhoneNumbers.length > 0">
                        <v-list-item-icon class="mr-3">
                          <v-icon color="success">mdi-check-circle</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">
                            Phone Numbers Ready
                          </v-list-item-title>
                          <v-list-item-subtitle class="caption">
                            {{ selectedPhoneNumbers.length }} number(s)
                            configured
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>

                    <v-divider class="my-4"></v-divider>

                    <div class="text-center">
                      <p class="modern-p mb-3">
                        You can now make and receive calls using GoDial's cloud
                        dialer!
                      </p>
                      <v-btn color="primary" large depressed @click="finish">
                        <v-icon left>mdi-check</v-icon>
                        Get Started
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-stepper-content>
      </v-stepper-items>
    </v-stepper>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="4000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "TwilioAuthDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      step: 1,

      // Twilio Connection
      twilioConnecting: false,
      twilioConnected: false,
      twilioOAuthAccountSid: null,
      twilioError: null,
      twilioAccountInfo: {},
      twilioPhoneNumbers: [],
      twilioSipDomains: [],
      twilioElasticTrunks: [],
      twilioNeedsSetup: [],
      twilioCarrier: null,

      // Trunk Management
      trunkMode: "existing", // 'existing' or 'create'
      selectedTrunkSid: null,
      newTrunkName: "",
      trunkLoading: false,
      trunkRefreshing: false,
      trunkCreating: false,
      trunkConfiguring: false,
      trunkError: null,
      trunkSuccess: null,
      // Trunk credentials config (auto-generated)
      trunkCredentialUsername: "",
      trunkCredentialPassword: "",
      pbxServerIp: process.env.VUE_APP_PBX_SERVER_IP || "13.234.45.114", // GoDial PBX Server IP (env-based)

      // Phone Numbers
      selectedPhoneNumbers: [],
      trunkPhoneNumbers: [], // Numbers already assigned to the trunk
      phoneNumbersLoading: false,
      phoneNumbersRefreshing: false,
      phoneNumbersSaving: false,
      phoneNumbersError: null,
      phoneNumbersSuccess: null,
      defaultRoutingType: "unassigned",
      routingTypes: [
        {
          text: "Unassigned",
          value: "unassigned",
          icon: "mdi-phone-off",
          description: "Number not routed to any agent",
        },
        {
          text: "Specific Agent",
          value: "agent",
          icon: "mdi-account",
          description: "Route to a specific agent",
        },
        {
          text: "Random Agent",
          value: "random-agent",
          icon: "mdi-shuffle-variant",
          description: "Route randomly to available agents",
        },
      ],

      // Snackbar
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT"]),

    // Check if the selected trunk already has connected numbers
    trunkHasConnectedNumbers() {
      if (!this.twilioPhoneNumbers || this.twilioPhoneNumbers.length === 0) {
        return false;
      }
      return this.twilioPhoneNumbers.some(
        (number) => number.trunkSid === this.selectedTrunkSid
      );
    },

    // Filter phone numbers based on trunk connection status
    // If trunk has connected numbers: only show those connected numbers
    // If trunk has no connected numbers: only show unconnected numbers (user can select one)
    availablePhoneNumbers() {
      if (!this.twilioPhoneNumbers || this.twilioPhoneNumbers.length === 0) {
        return [];
      }

      if (this.trunkHasConnectedNumbers) {
        // Trunk already has numbers - only show connected ones
        return this.twilioPhoneNumbers.filter(
          (number) => number.trunkSid === this.selectedTrunkSid
        );
      } else {
        // Trunk has no numbers - show only unconnected numbers
        return this.twilioPhoneNumbers.filter(
          (number) =>
            !number.trunkSid ||
            number.trunkSid === null ||
            number.trunkSid === ""
        );
      }
    },
  },
  watch: {
    value(val) {
      this.dialog = val;
      if (val) {
        this.checkTwilioConnectRedirect();
      }
    },
    dialog(val) {
      this.$emit("input", val);
    },
  },
  mounted() {
    this.checkTwilioConnectRedirect();
  },
  methods: {
    // Navigation Methods
    goToStep2() {
      this.step = 2;
      // Refresh elastic trunks if not already loaded
      if (!this.twilioElasticTrunks || this.twilioElasticTrunks.length === 0) {
        this.refreshElasticTrunks();
        // Default to create mode if no trunks
        this.trunkMode = "create";
      } else {
        // Set to existing mode if trunks are available
        this.trunkMode = "existing";
        if (!this.selectedTrunkSid) {
          // Auto-select first trunk if available
          this.selectedTrunkSid = this.twilioElasticTrunks[0].sid;
        }
      }
    },

    goToStep3() {
      this.step = 3;
      // Refresh phone numbers if not already loaded
      if (!this.twilioPhoneNumbers || this.twilioPhoneNumbers.length === 0) {
        this.refreshPhoneNumbers();
      } else if (
        this.twilioPhoneNumbers.length > 0 &&
        !this.selectedPhoneNumber
      ) {
        // Auto-select first phone number if available
        this.selectedPhoneNumber =
          this.twilioPhoneNumbers[0].phoneNumber ||
          this.twilioPhoneNumbers[0].phone_number;
      }
    },

    // Twilio OAuth - Same logic as welcomePopup
    async connectWithTwilioOAuth() {
      this.twilioConnecting = true;
      this.twilioError = null;

      try {
        const response = await this.$http.get(
          this.ENDPOINT +
            "/pbx/twilio-connect-url?companyId=" +
            this.$store.state.user.companyId
        );

        const data = response.body;

        if (!data || !data.ok || !data.authUrl) {
          throw new Error(
            (data && data.error) || "Failed to get Twilio authorization URL"
          );
        }

        console.log(
          "Opening Twilio Connect authorization popup:",
          data.authUrl
        );

        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(
          data.authUrl,
          "TwilioConnect",
          "width=" +
            width +
            ",height=" +
            height +
            ",left=" +
            left +
            ",top=" +
            top +
            ",resizable=yes,scrollbars=yes"
        );

        if (!popup) {
          throw new Error(
            "Popup was blocked. Please allow popups for this site and try again."
          );
        }

        const self = this;

        const messageHandler = function(event) {
          if (event.data && event.data.type === "TWILIO_CONNECT_CALLBACK") {
            console.log("Received Twilio Connect callback:", event.data);

            window.removeEventListener("message", messageHandler);

            if (popupCheckInterval) {
              clearInterval(popupCheckInterval);
            }

            self.twilioConnecting = false;

            if (event.data.error) {
              self.twilioError =
                "Authorization was denied: " + event.data.error;
            } else if (event.data.accountSid) {
              self.processTwilioAccountSid(event.data.accountSid);
            }
          }
        };
        window.addEventListener("message", messageHandler);

        const popupCheckInterval = setInterval(function() {
          if (!popup || popup.closed) {
            clearInterval(popupCheckInterval);
            window.removeEventListener("message", messageHandler);

            if (self.twilioConnecting && !self.twilioConnected) {
              self.twilioConnecting = false;
            }
          }
        }, 500);

        setTimeout(function() {
          clearInterval(popupCheckInterval);
          window.removeEventListener("message", messageHandler);
          if (self.twilioConnecting && !self.twilioConnected) {
            self.twilioConnecting = false;
            self.twilioError = "Authorization timed out. Please try again.";
            if (popup && !popup.closed) {
              popup.close();
            }
          }
        }, 5 * 60 * 1000);
      } catch (error) {
        console.error("Twilio OAuth error:", error);
        let errorMessage = "Failed to start Twilio authorization";
        if (error.body && error.body.error && error.body.error.message) {
          errorMessage = error.body.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.twilioError = errorMessage;
        this.twilioConnecting = false;
      }
    },

    async checkTwilioConnectRedirect() {
      const urlParams = new URLSearchParams(window.location.search);
      const accountSid = urlParams.get("AccountSid");
      const state = urlParams.get("state");
      const error = urlParams.get("error");

      if (!accountSid && !state && !error) {
        return;
      }

      console.log("Twilio Connect callback detected in popup");

      const url = new URL(window.location.href);
      url.searchParams.delete("AccountSid");
      url.searchParams.delete("state");
      url.searchParams.delete("error");
      window.history.replaceState({}, document.title, url.pathname + url.hash);

      if (window.opener) {
        const messageData = {
          type: "TWILIO_CONNECT_CALLBACK",
          accountSid: accountSid,
          state: state,
          error: error,
        };
        console.log("Sending Twilio data to parent window:", messageData);
        window.opener.postMessage(messageData, "*");

        setTimeout(function() {
          window.close();
        }, 500);
        return;
      }

      if (error) {
        console.error("Twilio Connect error:", error);
        this.twilioError = "Authorization was denied: " + error;
        this.dialog = true;
        this.step = 1;
        return;
      }

      if (accountSid) {
        await this.processTwilioAccountSid(accountSid);
      }
    },

    async processTwilioAccountSid(accountSid) {
      console.log("Captured Twilio AccountSid from OAuth:", accountSid);

      this.dialog = true;
      this.step = 1;
      this.twilioConnecting = true;
      this.twilioError = null;
      this.twilioOAuthAccountSid = accountSid;

      this.showSnackbar("Account verified! Completing connection...", "info");

      try {
        const response = await this.$http.post(
          this.ENDPOINT + "/pbx/process-twilio-connect",
          {
            companyId: this.$store.state.user.companyId,
            accountSid: accountSid,
          }
        );

        const result = response.body;
        console.log("Twilio Connect processed:", result);

        if (result && result.ok && result.data) {
          this.twilioConnected = true;
          this.twilioAccountInfo = {
            accountSid: result.data.accountSid,
            friendlyName: result.data.friendlyName,
            status: result.data.status,
          };
          this.twilioPhoneNumbers = result.data.phoneNumbers || [];
          this.twilioSipDomains = result.data.sipDomains || [];
          this.twilioElasticTrunks = result.data.elasticTrunks || [];
          this.twilioNeedsSetup = result.data.needsSetup || [];
          this.twilioCarrier = result.data.carrier || null;

          this.twilioOAuthAccountSid = null;
          this.showSnackbar("Successfully connected to Twilio!", "success");

          // Auto-select first trunk if available
          if (this.twilioElasticTrunks.length > 0) {
            this.selectedTrunkSid = this.twilioElasticTrunks[0].sid;
          }
        } else {
          this.twilioError =
            "Connection succeeded but data was incomplete. Please try again.";
        }
      } catch (err) {
        console.error("Error completing Twilio connection:", err);
        this.twilioError =
          err.body && err.body.error
            ? err.body.error.message
            : "Failed to complete Twilio connection. Please try again.";
      } finally {
        this.twilioConnecting = false;
      }
    },

    async completeTwilioOAuthConnection() {
      if (!this.twilioOAuthAccountSid) {
        this.twilioError = "No account selected. Please try connecting again.";
        return;
      }
      await this.processTwilioAccountSid(this.twilioOAuthAccountSid);
    },

    // Trunk Management
    async refreshElasticTrunks() {
      this.trunkRefreshing = true;
      this.trunkLoading =
        !this.twilioElasticTrunks || this.twilioElasticTrunks.length === 0;
      this.trunkError = null;

      try {
        const response = await this.$http.post(
          `${this.ENDPOINT}/pbx/list-elastic-trunks`,
          {
            companyId: this.$store.state.user.companyId,
          }
        );

        const result = response.body;
        if (result && result.trunks) {
          this.twilioElasticTrunks = result.trunks;

          if (this.twilioElasticTrunks.length > 0) {
            // Switch to existing mode if trunks found
            this.trunkMode = "existing";
            if (!this.selectedTrunkSid) {
              this.selectedTrunkSid = this.twilioElasticTrunks[0].sid;
            }
          }

          this.showSnackbar(
            `Found ${this.twilioElasticTrunks.length} SIP trunk(s)`,
            "success"
          );
        }
      } catch (err) {
        console.error("Error fetching elastic trunks:", err);
        this.trunkError =
          err.body && err.body.error
            ? err.body.error.message
            : "Failed to fetch SIP trunks";
      } finally {
        this.trunkRefreshing = false;
        this.trunkLoading = false;
      }
    },

    async createNewElasticTrunk() {
      if (!this.newTrunkName) {
        this.showSnackbar("Please enter a trunk name", "error");
        return;
      }

      this.trunkCreating = true;
      this.trunkError = null;

      try {
        const response = await this.$http.post(
          `${this.ENDPOINT}/pbx/create-elastic-trunk`,
          {
            companyId: this.$store.state.user.companyId,
            trunkName: this.newTrunkName,
          }
        );

        const result = response.body;
        if (result && result.ok && result.trunk) {
          this.twilioElasticTrunks.push(result.trunk);
          this.selectedTrunkSid = result.trunk.sid;
          this.trunkMode = "existing";
          this.newTrunkName = "";
          this.showSnackbar(
            `Created trunk: ${result.trunk.friendlyName}`,
            "success"
          );
        }
      } catch (err) {
        console.error("Error creating trunk:", err);
        this.trunkError =
          err.body && err.body.error
            ? err.body.error.message
            : "Failed to create SIP trunk";
      } finally {
        this.trunkCreating = false;
      }
    },

    // Generate a random password for SIP credentials
    generateRandomPassword(length = 16) {
      const chars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%";
      let result = "";
      for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    },

    async useSelectedTrunk() {
      console.log("[SIP Trunk] useSelectedTrunk called");
      console.log("[SIP Trunk] Selected trunk SID:", this.selectedTrunkSid);

      if (!this.selectedTrunkSid) {
        this.trunkError = "Please select a trunk";
        this.showSnackbar("Please select a trunk", "error");
        return;
      }

      this.trunkConfiguring = true;
      this.trunkError = null;
      this.trunkSuccess = null;

      try {
        // Find the selected trunk details
        const selectedTrunk = this.twilioElasticTrunks.find(
          (t) => t.sid === this.selectedTrunkSid
        );
        console.log("[SIP Trunk] Selected trunk details:", selectedTrunk);

        if (!selectedTrunk) {
          this.trunkError = "Selected trunk not found";
          this.trunkConfiguring = false;
          return;
        }

        // Generate credentials for this trunk
        const companyId = this.$store.state.user.companyId;
        this.trunkCredentialUsername = `godial_${companyId.substring(0, 8)}`;
        this.trunkCredentialPassword = this.generateRandomPassword();

        console.log("[SIP Trunk] Generated credentials:", {
          username: this.trunkCredentialUsername,
          passwordLength: this.trunkCredentialPassword.length,
          pbxServerIp: this.pbxServerIp,
        });

        // Step 1: Configure trunk with credentials and IP ACL
        console.log(
          "[SIP Trunk] Step 1: Configuring trunk credentials and IP ACL..."
        );
        const credentialResponse = await this.$http.post(
          `${this.ENDPOINT}/pbx/configure-trunk-credentials`,
          {
            companyId: companyId,
            trunkSid: this.selectedTrunkSid,
            credentialUsername: this.trunkCredentialUsername,
            credentialPassword: this.trunkCredentialPassword,
            pbxServerIp: this.pbxServerIp,
          }
        );

        console.log(
          "[SIP Trunk] Credential configuration response:",
          credentialResponse.body
        );

        if (!credentialResponse.body || !credentialResponse.body.ok) {
          const errMsg =
            credentialResponse.body && credentialResponse.body.error
              ? credentialResponse.body.error
              : "Failed to configure trunk credentials";
          throw new Error(errMsg);
        }

        console.log(
          "[SIP Trunk] ✅ Credentials and IP ACL configured successfully"
        );

        // Step 2: Save the trunk configuration
        console.log("[SIP Trunk] Step 2: Saving trunk configuration...");
        const response = await this.$http.post(
          `${this.ENDPOINT}/pbx/save-trunk-configuration?companyId=${companyId}`,
          {
            carrier: {
              type: "twilio-elastic-trunk",
              trunkSid: this.selectedTrunkSid,
              trunkName: selectedTrunk.friendlyName,
              domainName: selectedTrunk.domainName,
              accountSid: this.twilioAccountInfo
                ? this.twilioAccountInfo.accountSid
                : null,
              credentialUsername: this.trunkCredentialUsername,
              credentialPassword: this.trunkCredentialPassword,
              pbxServerIp: this.pbxServerIp,
              register: false, // Twilio Elastic SIP Trunks don't need REGISTER
            },
            dids: selectedTrunk.phoneNumbers || [],
          }
        );

        console.log(
          "[SIP Trunk] Save trunk configuration response:",
          response.body
        );

        const result = response.body;
        if (result && result.ok) {
          this.trunkSuccess = "SIP Trunk configured successfully!";
          this.showSnackbar("SIP Trunk configured for GoDial!", "success");

          console.log("[SIP Trunk] ✅ Trunk configuration saved successfully");

          // Update the twilioCarrier
          this.twilioCarrier = {
            type: "twilio-elastic-trunk",
            trunkSid: this.selectedTrunkSid,
            credentialUsername: this.trunkCredentialUsername,
          };
          
          // Refresh PBX data in Vuex store to update dialer visibility
          await this.$store.dispatch('refreshPbxData');

          // Proceed to next step and load phone numbers
          console.log("[SIP Trunk] Proceeding to step 3...");
          setTimeout(() => {
            this.step = 3;
            this.refreshPhoneNumbers();
          }, 1500);
        } else {
          const saveErrMsg =
            result && result.error
              ? result.error
              : "Failed to save trunk configuration";
          throw new Error(saveErrMsg);
        }
      } catch (err) {
        console.error("Error configuring trunk:", err);
        this.trunkError =
          err.body && err.body.error
            ? err.body.error.message
            : err.message || "Failed to configure trunk";
        this.showSnackbar(this.trunkError, "error");
      } finally {
        this.trunkConfiguring = false;
      }
    },

    // Phone Numbers Management
    async refreshPhoneNumbers() {
      console.log("[Phone Numbers] Refreshing phone numbers...");
      this.phoneNumbersRefreshing = true;
      this.phoneNumbersLoading = this.twilioPhoneNumbers.length === 0;
      this.phoneNumbersError = null;

      try {
        const companyId = this.$store.state.user.companyId;

        // Fetch all phone numbers from Twilio account
        const response = await this.$http.get(
          `${this.ENDPOINT}/pbx/twilio-phone-numbers?companyId=${companyId}`
        );

        console.log("[Phone Numbers] Response:", response.body);

        if (response.body && response.body.ok) {
          this.twilioPhoneNumbers = response.body.phoneNumbers || [];

          // Pre-select numbers that are already assigned to the selected trunk
          if (this.selectedTrunkSid) {
            this.selectedPhoneNumbers = this.twilioPhoneNumbers
              .filter((p) => p.trunkSid === this.selectedTrunkSid)
              .map((p) => p.phoneNumber);

            // Also populate trunkPhoneNumbers for reference
            this.trunkPhoneNumbers = this.twilioPhoneNumbers.filter(
              (p) => p.trunkSid === this.selectedTrunkSid
            );
          }

          console.log(
            "[Phone Numbers] Loaded:",
            this.twilioPhoneNumbers.length,
            "numbers"
          );
          console.log(
            "[Phone Numbers] Pre-selected (on trunk):",
            this.selectedPhoneNumbers.length
          );

          this.showSnackbar(
            `Found ${this.twilioPhoneNumbers.length} phone number(s)`,
            "success"
          );
        } else {
          throw new Error(
            response.body.error || "Failed to fetch phone numbers"
          );
        }
      } catch (err) {
        console.error("[Phone Numbers] Error:", err);
        let errMsg = "Failed to load phone numbers";
        if (err && err.body && err.body.error && err.body.error.message) {
          errMsg = err.body.error.message;
        } else if (err && err.message) {
          errMsg = err.message;
        }
        this.phoneNumbersError = errMsg;
      } finally {
        this.phoneNumbersRefreshing = false;
        this.phoneNumbersLoading = false;
      }
    },

    isNumberAssignedToTrunk(phoneNumber) {
      // Check if this number's trunkSid matches the selected trunk
      const number = this.twilioPhoneNumbers.find(
        (p) => p.phoneNumber === phoneNumber
      );
      return number && number.trunkSid === this.selectedTrunkSid;
    },

    async assignNumberToTrunk(phoneNumber) {
      console.log("[Phone Numbers] Assigning number to trunk:", phoneNumber);

      if (!this.selectedTrunkSid) {
        this.showSnackbar("No trunk selected", "error");
        return;
      }

      try {
        const companyId = this.$store.state.user.companyId;

        const response = await this.$http.post(
          `${this.ENDPOINT}/pbx/assign-phone-to-trunk`,
          {
            companyId: companyId,
            trunkSid: this.selectedTrunkSid,
            phoneNumber: phoneNumber,
          }
        );

        if (response.body && response.body.ok) {
          // Add to trunk numbers list
          const phoneObj = this.twilioPhoneNumbers.find(
            (p) => p.phoneNumber === phoneNumber
          );
          if (phoneObj) {
            this.trunkPhoneNumbers.push(phoneObj);
          }

          // Add to selected if not already
          if (!this.selectedPhoneNumbers.includes(phoneNumber)) {
            this.selectedPhoneNumbers.push(phoneNumber);
          }

          this.showSnackbar(
            "Phone number assigned to trunk successfully",
            "success"
          );
        } else {
          throw new Error(response.body.error || "Failed to assign number");
        }
      } catch (err) {
        console.error("[Phone Numbers] Error assigning number:", err);
        let errMsg = "Failed to assign number to trunk";
        if (err && err.body && err.body.error && err.body.error.message) {
          errMsg = err.body.error.message;
        } else if (err && err.message) {
          errMsg = err.message;
        }
        this.showSnackbar(errMsg, "error");
      }
    },

    async savePhoneNumbersConfig() {
      console.log("[Phone Numbers] Saving phone numbers configuration...");
      this.phoneNumbersSaving = true;
      this.phoneNumbersError = null;
      this.phoneNumbersSuccess = null;

      try {
        const companyId = this.$store.state.user.companyId;

        // Auto-assign any selected numbers that are not yet connected to the trunk
        for (const phoneNumber of this.selectedPhoneNumbers) {
          const phoneObj = this.twilioPhoneNumbers.find(
            (p) => p.phoneNumber === phoneNumber
          );
          // If number is not connected to this trunk, assign it first
          if (phoneObj && phoneObj.trunkSid !== this.selectedTrunkSid) {
            console.log("[Phone Numbers] Auto-assigning number to trunk:", phoneNumber);
            try {
              const assignResponse = await this.$http.post(
                `${this.ENDPOINT}/pbx/assign-phone-to-trunk`,
                {
                  companyId: companyId,
                  trunkSid: this.selectedTrunkSid,
                  phoneNumber: phoneNumber,
                }
              );
              if (!assignResponse.body || !assignResponse.body.ok) {
                throw new Error((assignResponse.body && assignResponse.body.error) || "Failed to assign number");
              }
              console.log("[Phone Numbers] Number assigned successfully:", phoneNumber);
            } catch (assignErr) {
              console.error("[Phone Numbers] Failed to assign number:", assignErr);
              throw new Error(`Failed to assign ${phoneNumber} to trunk`);
            }
          }
        }

        // Prepare DID configurations
        const dids = this.selectedPhoneNumbers.map((phoneNumber) => {
          const phoneObj = this.twilioPhoneNumbers.find(
            (p) => p.phoneNumber === phoneNumber
          );
          return {
            number: phoneNumber,
            normalizedNumber: phoneNumber.replace(/[^0-9+]/g, ""),
            description: phoneObj ? phoneObj.friendlyName : "",
            routing: {
              type: this.defaultRoutingType,
              agentId: null,
              agentIds: [],
              fallback: "voicemail",
            },
          };
        });

        console.log("[Phone Numbers] DIDs to save:", dids);

        // Save DID configuration
        const response = await this.$http.post(
          `${this.ENDPOINT}/pbx/save-did-configuration?companyId=${companyId}`,
          {
            dids: dids,
            trunkSid: this.selectedTrunkSid,
          }
        );

        console.log("[Phone Numbers] Save response:", response.body);

        if (response.body && response.body.ok) {
          this.phoneNumbersSuccess = "Phone numbers configured successfully!";
          this.showSnackbar("Phone numbers saved!", "success");

          // Proceed to completion step
          setTimeout(() => {
            this.step = 4;
          }, 1000);
        } else {
          throw new Error(
            response.body.error || "Failed to save DID configuration"
          );
        }
      } catch (err) {
        console.error("[Phone Numbers] Save error:", err);
        let saveErrMsg = "Failed to save configuration";
        if (err && err.body && err.body.error && err.body.error.message) {
          saveErrMsg = err.body.error.message;
        } else if (err && err.message) {
          saveErrMsg = err.message;
        }
        this.phoneNumbersError = saveErrMsg;
        this.showSnackbar(saveErrMsg, "error");
      } finally {
        this.phoneNumbersSaving = false;
      }
    },

    // Helpers
    getSelectedTrunkName() {
      const trunk = this.twilioElasticTrunks.find(
        (t) => t.sid === this.selectedTrunkSid
      );
      return trunk ? trunk.friendlyName : this.selectedTrunkSid;
    },

    showSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    closeDialog() {
      this.dialog = false;
      this.$emit("close");
    },

    finish() {
      this.$emit("complete", {
        twilioConnected: this.twilioConnected,
        accountInfo: this.twilioAccountInfo,
        trunkSid: this.selectedTrunkSid,
        phoneNumbers: this.selectedPhoneNumbers,
      });
      this.closeDialog();
    },
  },
};
</script>

<style>
/* Global Dialog Styles (Not Scoped) - Must match content-class */
.twilio-auth-dialog {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
  max-height: 85vh !important;
  margin: 24px !important;
}

.twilio-auth-dialog .v-stepper {
  border-radius: 20px !important;
  display: flex;
  flex-direction: column;
  max-height: calc(85vh - 48px) !important;
  overflow: hidden;
}

.twilio-auth-dialog .v-stepper__header {
  flex-shrink: 0;
  box-shadow: none;
  background: white;
  border-bottom: 1px solid #e8ecef;
  padding: 16px 24px;
}

.twilio-auth-dialog .v-stepper__items {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.twilio-auth-dialog .v-stepper__content {
  flex: 1;
  overflow: hidden !important;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.twilio-auth-dialog .v-stepper__wrapper {
  height: 100%;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.twilio-auth-dialog .v-stepper__content > .v-card,
.twilio-auth-dialog .v-stepper__wrapper > .v-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  border-radius: 0 !important;
}

.twilio-auth-dialog .v-stepper__content > .v-card > .v-card__text,
.twilio-auth-dialog .v-stepper__wrapper > .v-card > .v-card__text {
  flex: 1;
  overflow-y: auto !important;
  padding: 24px !important;
  min-height: 0;
}

.twilio-auth-dialog .v-stepper__content > .v-card > .v-card__actions,
.twilio-auth-dialog .v-stepper__wrapper > .v-card > .v-card__actions {
  flex-shrink: 0;
  background: #f8fafb;
  border-top: 1px solid #e8ecef;
  padding: 16px 24px !important;
}

/* Step Label Styles */
.twilio-auth-dialog .step-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #5a6c7d;
}

/* Mobile Responsive */
@media (max-width: 600px) {
  .twilio-auth-dialog {
    margin: 8px !important;
    max-height: 90vh !important;
    border-radius: 16px !important;
  }

  .twilio-auth-dialog .v-stepper {
    max-height: calc(90vh - 16px) !important;
  }

  .twilio-auth-dialog .v-stepper__header {
    flex-wrap: wrap;
    padding: 8px !important;
  }

  .twilio-auth-dialog .v-stepper__step {
    flex-basis: auto !important;
    padding: 4px 8px !important;
  }

  .twilio-auth-dialog .v-divider {
    display: none !important;
  }
}
</style>

<style scoped>
/* Scoped Styles for Component Elements */

/* Setup Card - Clean White Background */
.setup-card {
  background: #fff !important;
}

/* Typography */
.modern-h1 {
  font-size: 26px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1.3;
  letter-spacing: -0.3px;
}

.modern-h3 {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
}

.modern-p {
  font-size: 14px;
  line-height: 1.6;
  color: #5a6c7d;
  margin-bottom: 0;
}

/* Step Badge */
.step-badge {
  display: flex;
  justify-content: center;
}

.step-badge .v-chip {
  font-size: 10px !important;
  letter-spacing: 0.8px;
  padding: 0 10px !important;
  height: 22px !important;
  border-radius: 50px !important;
}

/* Twilio Form Card */
.twilio-form-card {
  border-radius: 16px !important;
  border: 1px solid #e8ecef !important;
  max-width: 520px;
  margin: 0 auto;
}

/* Inner Content Cards - Rounded */
.twilio-form-card.v-card,
.setup-card .v-card.elevation-3 {
  border-radius: 16px !important;
}

.twilio-connect-btn {
  transition: all 0.3s ease;
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 12px !important;
}

.twilio-connect-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(242, 47, 70, 0.3);
}

/* Trunk Selection Card */
.trunk-selection-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
  border: 1px solid #e8ecef !important;
}

.trunk-selection-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #37cf37 !important;
}

.selected-trunk-card {
  border: 2px solid #37cf37 !important;
  background: rgba(55, 207, 55, 0.05) !important;
}

/* Phone Number Card */
.phone-number-card {
  transition: all 0.2s ease;
  border-radius: 10px !important;
}

.phone-number-card.selected-number {
  background: #f0fdf4;
  border-color: #37cf37 !important;
}

/* Dialog Footer */
.dialog-footer {
  background: #f8fafb;
  border-top: 1px solid #e8ecef;
}

.cancel-btn {
  color: #5a6c7d !important;
  font-weight: 500;
}

.cancel-btn:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}

.continue-btn {
  border-radius: 10px !important;
  font-weight: 600;
  padding-left: 32px !important;
  padding-right: 32px !important;
  box-shadow: 0 4px 12px rgba(55, 207, 55, 0.3);
}

.continue-btn:hover {
  box-shadow: 0 6px 16px rgba(55, 207, 55, 0.4);
  transform: translateY(-1px);
}
</style>
